import { useEffect, useRef, useState, useCallback } from "react";
import { Heart, Users, Send, Trash2, Pin, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Real-time-ish live engagement via fast polling (every 4s) + heartbeat (every 12s).
export const LiveChat = ({ streamId }) => {
  const { isAuthed, isSubscribed, isAdmin } = useAuth();
  const [viewers, setViewers] = useState(0);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [hasNew, setHasNew] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const lastTsRef = useRef("");
  const listRef = useRef(null);
  const hasMoreOlderRef = useRef(true);
  const loadingOlderRef = useRef(false);
  const restoreRef = useRef(null);
  const canChat = isAdmin || isSubscribed;

  const poll = useCallback(async () => {
    try {
      const { data } = await endpoints.liveEngagement(streamId);
      setViewers(data.viewers); setLikes(data.likes); setLiked(data.liked);
    } catch { /* noop */ }
    try {
      const { data } = await endpoints.liveChatGet(streamId, lastTsRef.current);
      if (data.pinned !== undefined) setPinned(data.pinned);
      if (data.messages?.length) {
        lastTsRef.current = data.messages[data.messages.length - 1].createdAt;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...data.messages.filter((m) => !seen.has(m.id))];
          return merged.slice(-200);
        });
      }
    } catch { /* noop */ }
  }, [streamId]);

  useEffect(() => {
    setMessages([]); lastTsRef.current = "";
    hasMoreOlderRef.current = true; restoreRef.current = null;
    poll();
    const t = setInterval(poll, 4000);
    let hb = null;
    if (isAuthed) {
      const beat = () => endpoints.liveHeartbeat(streamId).catch(() => {});
      beat();
      hb = setInterval(beat, 12000);
    }
    return () => { clearInterval(t); if (hb) clearInterval(hb); };
  }, [streamId, isAuthed, poll]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    // Restore scroll position after prepending older history (keep the viewport stable).
    if (restoreRef.current != null) {
      el.scrollTop = el.scrollHeight - restoreRef.current;
      restoreRef.current = null;
      return;
    }
    // Keep view pinned to newest ONLY when the reader is already at the bottom, so scrolling
    // back through history is never yanked away when new messages arrive.
    if (atBottom) {
      el.scrollTop = el.scrollHeight;
      setHasNew(false);
    } else {
      setHasNew(true);
    }
  }, [messages, atBottom]);

  // Infinite history: load older messages when the reader scrolls near the top.
  const loadOlder = useCallback(async () => {
    const el = listRef.current;
    if (!el || loadingOlderRef.current || !hasMoreOlderRef.current) return;
    const oldest = messages[0]?.createdAt;
    if (!oldest) return;
    loadingOlderRef.current = true; setLoadingOlder(true);
    try {
      const { data } = await endpoints.liveChatBefore(streamId, oldest);
      const older = data.messages || [];
      hasMoreOlderRef.current = !!data.hasMore;
      if (older.length) {
        restoreRef.current = el.scrollHeight - el.scrollTop; // distance from bottom, preserved across prepend
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...older.filter((m) => !seen.has(m.id)), ...prev];
        });
      }
    } catch { /* noop */ }
    finally { loadingOlderRef.current = false; setLoadingOlder(false); }
  }, [streamId, messages]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    if (el.scrollTop < 60) loadOlder();
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setAtBottom(near);
    if (near) setHasNew(false);
  };

  const jumpToLatest = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setAtBottom(true); setHasNew(false);
  };

  const toggleLike = async () => {
    if (!isAuthed) { window.dispatchEvent(new Event("open-auth")); return; }
    // optimistic
    setLiked((l) => !l); setLikes((n) => (liked ? n - 1 : n + 1));
    try { const { data } = await endpoints.liveLike(streamId); setLikes(data.likes); setLiked(data.liked); }
    catch { poll(); }
  };

  const send = async () => {
    const msg = text.trim();
    if (!msg) return;
    if (!isAuthed) { window.dispatchEvent(new Event("open-auth")); return; }
    if (!canChat) { window.dispatchEvent(new CustomEvent("open-subscribe")); return; }
    setSending(true);
    try {
      const { data } = await endpoints.liveChatPost(streamId, msg);
      setText("");
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data].slice(-200)));
      lastTsRef.current = data.createdAt;
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const del = async (id) => {
    try { await endpoints.adminDeleteChat(id); setMessages((prev) => prev.filter((m) => m.id !== id)); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };
  const pin = async (m) => {
    try { await endpoints.adminPinChat(m.id, !m.pinned); setPinned(m.pinned ? null : m); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="relative flex flex-col rounded-2xl border border-[#273042] bg-[#121620] overflow-hidden h-[560px] lg:h-[640px] max-h-[78vh]" data-testid="live-chat">
      <div className="flex items-center justify-between gap-2 px-4 h-14 border-b border-[#273042] shrink-0">
        <div className="flex items-center gap-2 text-white font-bold text-sm"><MessageCircle className="w-4 h-4 text-[#E11D2A]" /> Live chat</div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-slate-300 text-sm font-semibold" data-testid="live-viewers">
            <Users className="w-4 h-4 text-emerald-400" /> {viewers.toLocaleString()}
          </span>
          <button onClick={toggleLike} data-testid="live-like-btn" aria-label={liked ? "Unlike live stream" : "Like live stream"} aria-pressed={liked}
            className={`flex items-center gap-1.5 h-8 px-3 rounded-full text-sm font-bold transition-colors ${liked ? "bg-[#E11D2A] text-white" : "bg-white/5 text-slate-300 hover:text-white border border-[#273042]"}`}>
            <Heart className="w-4 h-4" fill={liked ? "currentColor" : "none"} /> <span data-testid="live-like-count">{likes.toLocaleString()}</span>
          </button>
        </div>
      </div>

      {pinned && (
        <div className="px-4 py-2.5 bg-[#2563EB]/10 border-b border-[#2563EB]/30 flex items-start gap-2" data-testid="live-pinned">
          <Pin className="w-3.5 h-3.5 text-[#2563EB] mt-0.5 shrink-0" />
          <div className="min-w-0"><span className="text-[#93B4FF] text-xs font-bold">{pinned.userName}</span>
            <p className="text-slate-200 text-sm break-words">{pinned.text}</p></div>
        </div>
      )}

      <div ref={listRef} onScroll={onScroll} style={{ overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
        className="relative flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3" data-testid="live-chat-messages">
        {loadingOlder && (
          <div className="flex justify-center py-1" data-testid="chat-loading-older"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>
        )}
        {messages.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-10">Be the first to say hello 👋</div>
        ) : messages.map((m) => (
          <div key={m.id} className="group flex items-start gap-2" data-testid={`chat-msg-${m.id}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${m.isAdmin ? "bg-[#E11D2A] text-white" : "bg-[#273042] text-slate-200"}`}>
              {(m.userName || "M").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className={`text-xs font-bold truncate ${m.isAdmin ? "text-[#E11D2A]" : "text-slate-300"}`}>{m.userName}</span>
                {m.isAdmin && <span className="text-[9px] font-extrabold uppercase bg-[#E11D2A]/20 text-[#E11D2A] px-1.5 rounded">Host</span>}
              </div>
              <p className="text-slate-200 text-sm break-words">{m.text}</p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => pin(m)} title="Pin" aria-label="Pin message" data-testid={`chat-pin-${m.id}`} className="w-6 h-6 rounded bg-white/5 text-slate-300 hover:text-[#2563EB] flex items-center justify-center"><Pin className="w-3 h-3" /></button>
                <button onClick={() => del(m.id)} title="Delete" aria-label="Delete message" data-testid={`chat-del-${m.id}`} className="w-6 h-6 rounded bg-white/5 text-slate-300 hover:text-red-400 flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {hasNew && !atBottom && (
        <button onClick={jumpToLatest} data-testid="chat-jump-latest"
          className="absolute left-1/2 -translate-x-1/2 bottom-[72px] z-10 h-8 px-4 rounded-full bg-[#E11D2A] text-white text-xs font-bold shadow-lg btn-glow flex items-center gap-1.5">
          New messages ↓
        </button>
      )}

      <div className="p-3 border-t border-[#273042] shrink-0">
        {canChat ? (
          <div className="flex items-center gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Send a message…" aria-label="Chat message" data-testid="live-chat-input" maxLength={500}
              className="flex-1 h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
            <button onClick={send} disabled={sending} data-testid="live-chat-send" aria-label="Send chat message"
              className="w-11 h-11 rounded-xl bg-[#E11D2A] hover:bg-[#C8121F] text-white flex items-center justify-center btn-glow disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        ) : (
          <button onClick={() => window.dispatchEvent(new CustomEvent(isAuthed ? "open-subscribe" : "open-auth"))} data-testid="live-chat-locked"
            className="w-full h-11 rounded-xl bg-white/5 border border-[#273042] text-slate-300 text-sm font-semibold hover:text-white">
            {isAuthed ? "Subscribe to join the chat" : "Sign in to join the chat"}
          </button>
        )}
      </div>
    </div>
  );
};
